import { RubyGemMark } from '../../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, RotateCcw, Receipt, Clock3, AlertTriangle, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { toastService } from '../../../services/toastService';
import {
  hardwareApi,
  type DeviceUnitRow, type DeviceModelRow, type UnitEvent,
} from '../../../services/hardwareApi';
import { mergeBuilds, isEnvAvailable, canInstall, allowedSources, defaultSource, type BuildRow, type InstallSource } from './installPlan';
import { listTimeZones, detectTimeZone, describeTimeZone } from './timezones';
import UnitDangerZone from './UnitDangerZone';
import UnitPickerCards from './UnitPickerCards';
import OverrideGate from './OverrideGate';
import { buildTargetLabel } from '@platform/shared-types';
import { parseDeviceFacts, primaryMac, formatMb, formatMhz, describeUsb, hasKnownPrinter, managementState, type DeviceFacts } from './deviceFacts';
import { DEVICE_PROFILES, profileById, profileForModel, catalogInstallProfileId, connectionFields, composeAddress, validateConnection, checkPeripherals } from './deviceProfiles';
import { detectStage } from '../../../services/aiControl/transport';

/**
 * IS THIS PAGE BEING SERVED BY A LOCAL VITE DEV SERVER?
 *
 * 🔴 This is not cosmetic. The installer endpoints this panel calls
 * (`/__device-install`, `/__published-builds`) are VITE MIDDLEWARE — they exist
 * only in vite.config.ts and only while `npm run dev` is running. On the hosted
 * consoles (admin.dev.rubytill.com, admin.rubytill.com) those paths fall through
 * to the SPA and answer `200 text/html`, which is why the build list has always
 * failed there and why pressing Install streamed `<!DOCTYPE html>` into the log
 * pane instead of touching a device.
 *
 * adb has to run on a machine that can reach the till's IP; a static CloudFront
 * page cannot spawn it. So a hosted console reads the registry through
 * hardware-service instead, and says plainly that the install step itself has to
 * be run from a local console.
 */
const IS_LOCAL_CONSOLE =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/**
 * THE LOCAL INSTALL AGENT — how a HOSTED console installs a device.
 *
 * adb must run on a machine that can reach the till's private address, which
 * CloudFront cannot. The agent (`npm run agent`) is a small server on this
 * operator's own machine that does exactly that one job. The page stays on
 * admin.rubytill.com, so the unit record, its timeline and its timestamps are
 * all written against PRODUCTION by the production console — which is the
 * reason this is not solved by running the console locally instead.
 *
 * Loopback is a potentially-trustworthy origin, so an HTTPS page is allowed to
 * call it; see the agent's Private Network Access note for the header that
 * makes Chrome actually send the request.
 */
const AGENT_ORIGIN = 'http://127.0.0.1:47591';

/**
 * Unit states an operator can pick up and install today. A unit that has not
 * reached the shop floor yet (`awaiting`/`processing`) is not offered — see
 * b1-lifecycle-report.md's state machine.
 *
 * `configured` and `packing` ARE offered, and deliberately: a build gets
 * re-flashed on the bench all the time — a bad install, a newer build before
 * the box goes out, a device reset for retesting — and until the order ships
 * that device is still in our hands. Excluding them meant a till that had been
 * configured once could never be touched again, which is not how the bench
 * works.
 *
 * Everything from `shipped` on is still excluded: the device has physically
 * left, so there is nothing here to install onto.
 */
const PICKABLE_STATES = ['in_stock', 'allocated', 'provisioning', 'configured', 'packing'] as const;

/**
 * An install option, as a switch rather than a checkbox.
 *
 * Track 44×24 with a 20px knob, 44px hit area, `role="switch"` — the platform's
 * switch geometry. `tone` is the only thing that varies: `danger` marks the
 * option that destroys data on the device.
 *
 * THE KNOB CARRIES AN EXPLICIT `left-0`, and it is not decoration.
 *
 * An `absolute` box with no `left` resolves to its STATIC position — where it
 * would have sat in flow. The knob's containing block is inside a <button>, and
 * the browser default `button { text-align: center }` is not reset by Tailwind's
 * preflight, so that static position is the centre of the track: a measured
 * `left: 22px` on a 44px track. The knob then rendered 22px further right than
 * every `translate-x-*` assumed — entirely outside the track, sitting on top of
 * the label's first letter. `left-0` pins the origin so the translate is the
 * only thing that moves it.
 */
function OptionToggle({
  on, onChange, label, hint, tone = 'default', disabled,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}) {
  const track = on
    ? (tone === 'danger' ? 'bg-ruby' : 'bg-emerald-500')
    : 'bg-stone-300';
  return (
    <div className="min-w-[15rem] max-w-sm">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className="group flex items-center gap-3 w-full text-left min-h-[44px] rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ruby disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className={`relative block w-11 h-6 shrink-0 rounded-full transition-colors ${track}`}>
          <span className={`absolute left-0 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
        </span>
        <span className={`text-xs font-bold ${on && tone === 'danger' ? 'text-ruby' : 'text-gray-800'}`}>{label}</span>
      </button>
      {hint && <p className="text-[11px] leading-snug text-gray-500 mt-0.5 ml-[3.5rem]">{hint}</p>}
    </div>
  );
}

/**
 * The canonical name for a build target. Guarded like every other runtime
 * shared-types read: a stale Vite pre-bundle must fall back to the registry's
 * published name rather than throwing on the install screen.
 *
 * Returns '' when shared-types does not know the id, so the caller keeps
 * whatever the registry published for it.
 */
function buildTargetLabelSafe(id: string): string {
  try {
    if (typeof buildTargetLabel === 'function') {
      const label = buildTargetLabel(id);
      return label && label !== id && label !== '—' ? label : '';
    }
  } catch { /* fall through to the published name */ }
  return '';
}

/** A unit carrying a verified install is being RE-flashed, not set up fresh. */
const isReinstall = (u?: DeviceUnitRow | null): boolean => !!u?.osInstall;

/**
 * Provision a device: pick the PHYSICAL UNIT it is, connect/pair it (the
 * original local-installer flow: build + install app + apply published
 * branding), verify + record what that connection actually reported, then
 * log the install actions onto the unit's timeline.
 *
 * Only works when running the super-admin UI locally (npm run dev); the
 * deployed super-admin has no local server to run the installer script.
 *
 * HONESTY NOTE on auto-fill: the installer (`tools/install-device.sh`) only
 * ever echoes one identifying line — `✓ device: $SERIAL` — where $SERIAL is
 * either a real adb hardware serial (USB / already-paired device) OR just
 * `<ip>:5555` when connecting by IP (that's a network address, not a device
 * serial, and is never offered as one). MAC address, OS version and app
 * build are NOT reported by this script at all — those fields start empty
 * and stay manual-entry always. Nothing here invents a value the connection
 * didn't actually report.
 */
export default function DeviceInstallPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectUnitId = searchParams.get('unitId') || '';

  // -- unit picker -----------------------------------------------------------
  const [units, setUnits] = useState<DeviceUnitRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [unitId, setUnitId] = useState<string>(preselectUnitId);
  const [timeline, setTimeline] = useState<UnitEvent[]>([]);

  const loadUnits = useCallback(async () => {
    setUnitsLoading(true);
    try {
      const [lists, m] = await Promise.all([
        Promise.all(PICKABLE_STATES.map((state) => hardwareApi.listUnits({ state, limit: 300 }))),
        hardwareApi.listModels().catch(() => ({ items: [] as DeviceModelRow[] })),
      ]);
      let merged = lists.flatMap((r) => r.items || []);
      // A preselected unit (from Pending configuration's "Configure →", or a
      // direct link) must be selectable even if it fell outside the first
      // page of its state bucket — fetch it directly rather than dropping it.
      if (preselectUnitId && !merged.some((u) => u.unitId === preselectUnitId)) {
        try {
          const single = await hardwareApi.getUnit(preselectUnitId);
          merged = [single.unit, ...merged];
        } catch { /* unit may not exist / may not be pickable — the dropdown just won't show it */ }
      }
      setUnits(merged);
      setModels(m.items || []);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to load units');
    } finally {
      setUnitsLoading(false);
    }
  }, [preselectUnitId]);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setRowsLoading(true);
      setRowsError(false);
      // A fetch that genuinely fails (offline, dev server down) must not read the
      // same as "nothing published yet" — track that distinctly so the inline
      // note can tell the operator which one it is.
      let failed = false;
      const fetchEnv = async (env: 'dev' | 'prod' | 'local') => {
        try {
          const r = await fetch(`/__published-builds?env=${env}`);
          if (!r.ok) throw new Error(String(r.status));
          return await r.json();
        } catch {
          failed = true;
          return { targets: [] };
        }
      };
      try {
        // A HOSTED CONSOLE HAS NO VITE MIDDLEWARE — it reads the registry for
        // its own stage through hardware-service, which fetches the CDN server
        // side (the CDN sends no CORS headers, so the browser cannot). It gets
        // one environment because that is the only one it may install.
        if (!IS_LOCAL_CONSOLE) {
          let targets: BuildRow[] = [];
          try {
            const res = await hardwareApi.listPosBuilds();
            targets = mergeBuilds(
              res.env === 'dev' ? res.targets : [],
              res.env === 'prod' ? res.targets : [],
            );
          } catch {
            failed = true;
          }
          if (!alive) return;
          setRows(
            targets
              .map((row) => ({ ...row, displayName: buildTargetLabelSafe(row.id) || row.displayName || row.id, local: false }))
              .sort((a, b) => a.displayName.localeCompare(b.displayName)),
          );
          setRowsError(failed);
          return;
        }
        const [d, p, l] = await Promise.all([fetchEnv('dev'), fetchEnv('prod'), fetchEnv('local')]);
        if (!alive) return;
        // `local` needs no published artifact — it compiles the working tree —
        // so its targets come from the repo's declared build-targets.json, not
        // from a CI-published registry. A target that ONLY exists there still
        // needs a row so it can be picked and installed via `local`, even when
        // dev/prod have never published anything.
        const merged = mergeBuilds(d.targets || [], p.targets || []);
        const byId = new Map(merged.map((row) => [row.id, row]));
        const localTargets: { id: string; displayName: string }[] = Array.isArray(l.targets) ? l.targets : [];
        // Only a target THIS checkout actually declares in build-targets.json
        // (the `?env=local` list) is real for `local` — a row that exists
        // purely because dev/prod published it is not, even though it has a
        // row here; `isEnvAvailable` reads this flag, not row presence.
        const localIds = new Set(localTargets.map((t) => t.id));
        for (const t of localTargets) {
          if (!byId.has(t.id)) byId.set(t.id, { id: t.id, displayName: t.displayName });
        }
        // THE NAME COMES FROM SHARED-TYPES, NOT FROM THE REGISTRY.
        //
        // `targets.json` carries a `displayName` written by whichever POS deploy
        // last published it, so it is a SNAPSHOT of the name at that moment — a
        // rename shows up here only after the next deploy, and dev and prod can
        // disagree with each other and with the device editor in the meantime.
        // That is how this screen still read "K10 (dual screen)" while the
        // catalog offered "RUBY-ANDROID-POS-BUILD" for the same target.
        // The id is the only thing the registry is authoritative about; the
        // label is looked up, and falls back to whatever was published for a
        // target shared-types has never heard of.
        const withLocalFlag = [...byId.values()].map((row) => ({
          ...row,
          displayName: buildTargetLabelSafe(row.id) || row.displayName || row.id,
          local: localIds.has(row.id),
        }));
        setRows(withLocalFlag.sort((a, b) => a.displayName.localeCompare(b.displayName)));
        setRowsError(failed);
      } finally {
        if (alive) setRowsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Every selection is per-unit. Switching units must clear the build, the
  // source and the previous run's output — a build left over from the last
  // unit is an implicit default, which is exactly what this screen must never
  // have (it would also let unit A's install banner/log linger on unit B).
  useEffect(() => {
    // The override does NOT survive a unit change: a code accepted for unit A
    // is not consent to deviate on unit B, and a build left selected from the
    // last unit is the implicit default this screen must never have.
    setBuildOverride('');
    setOverrideUnlocked(false);
    // Nor does consent to ship one unit unmanaged carry onto the next device.
    setUnmanagedArmed(false);
    setUnmanagedAccepted(false);
    setSource('dev');
    setLog('');
    setConnectedSerial(null);
    setInstallExitCode(null);
  }, [unitId]);

  const modelName = useCallback(
    (modelId?: string) => (modelId && models.find((m) => m.modelId === modelId)?.modelName) || modelId || 'Unknown model',
    [models],
  );

  const unit = useMemo(() => units.find((u) => u.unitId === unitId) || null, [units, unitId]);

  const selectUnit = (id: string | null) => {
    setUnitId(id || '');
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (id) p.set('unitId', id); else p.delete('unitId');
      return p;
    }, { replace: true });
  };

  const loadTimeline = useCallback(async (id: string) => {
    try {
      const res = await hardwareApi.getUnitTimeline(id, { limit: 20 });
      setTimeline(res.items || []);
    } catch {
      setTimeline([]); // not deployed yet, or a genuine failure — either way, honest empty rather than a broken panel
    }
  }, []);

  useEffect(() => {
    if (unit) loadTimeline(unit.unitId);
    else setTimeline([]);
  }, [unit, loadTimeline]);

  const recordEvent = useCallback(async (body: { kind: string; label: string; source?: 'system' | 'admin' | 'device'; meta?: Record<string, any> }) => {
    if (!unit) return;
    try {
      await hardwareApi.addUnitEvent(unit.unitId, body);
      loadTimeline(unit.unitId);
    } catch (e: any) {
      // Best-effort — the install itself already happened; a logging failure
      // must not read as the install having failed.
      toastService.warning(`Installed, but couldn't log "${body.label}" to the unit's history: ${e?.message || 'unknown error'}`);
    }
  }, [unit, loadTimeline]);

  // -- connect / install (original local-installer flow) ---------------------
  // Source, NOT environment: 'local' compiles this working tree, 'dev'/'prod'
  // install the APK CI published. WHICH ONES ARE OFFERED IS DECIDED BY THIS
  // CONSOLE'S OWN URL, not by the operator — see `allowedSources`. A hosted
  // console gets exactly one, so it cannot start on, or switch to, the wrong
  // signing key.
  /*
    Agent discovery. `null` = still looking, `false` = not running, string = its
    origin. A hosted console needs it to install at all, so the answer drives
    both the Install button and the notice above it — and "still looking" must
    never read as "not running", or an operator is told to start something that
    is already up.
  */
  const [agent, setAgent] = useState<string | null | false>(IS_LOCAL_CONSOLE ? false : null);
  useEffect(() => {
    if (IS_LOCAL_CONSOLE) return; // the dev server IS the installer here
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${AGENT_ORIGIN}/health`, { mode: 'cors' });
        const j = await r.json();
        if (alive) setAgent(j?.agent === 'rubytill-install-agent' ? AGENT_ORIGIN : false);
      } catch {
        // A refused connection and a blocked private-network preflight are the
        // same opaque TypeError here. Both mean "cannot use it", which is all
        // this needs to decide.
        if (alive) setAgent(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const sources = useMemo(() => allowedSources(detectStage(), IS_LOCAL_CONSOLE), []);
  const [source, setSource] = useState<InstallSource>(() => defaultSource(detectStage(), IS_LOCAL_CONSOLE));
  /**
   * THE BUILD. RESOLVED FROM THE DEVICE, NOT ASKED FOR (2026-09-18).
   *
   * This used to start empty and was never pre-filled from the model's catalog
   * `buildTarget`, on the reasoning that catalog rows are entered by people
   * whose data is not verified and an accepted default is how a wrong binary
   * reaches a device.
   *
   * That held while there was exactly ONE terminal model — the only build on
   * the list was the right one, so hand-picking cost nothing and proved nothing.
   * With a second model it inverts: the operator is now choosing between builds
   * from a list that names no device, every single install, and the wrong choice
   * flashes the wrong OS onto a customer's till and is not noticed until the
   * machine misbehaves in a shop.
   *
   * So the answer is given ONCE, on the device, by someone holding the device,
   * and resolved here. `buildOverride` is the deliberate exception and is gated
   * behind the unit override code — a STRONGER gate than the free choice this
   * replaces, which had none at all.
   *
   * The SOURCE (local/dev/prod) is still picked by hand every time and is never
   * defaulted from anything: installing a dev-signed build onto a customer's
   * unit is the mistake that costs a bench trip, and no catalog row knows which
   * of the three this particular run is for.
   */
  const [buildOverride, setBuildOverride] = useState<string>('');
  const [overrideUnlocked, setOverrideUnlocked] = useState(false);
  const [rows, setRows] = useState<BuildRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(false);
  const [confirmProd, setConfirmProd] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [ip, setIp] = useState('');
  /*
    Used to be OPERATOR-CHOSEN ONLY: the unit row records what was INSTALLED
    (`osInstall`) but not what the hardware reported itself to be, and the
    catalog model carried no deliberate hardware answer to seed from — so
    asking was cheaper and more honest than guessing.

    That changed once the catalog model itself got a curated
    `appProfile.installProfileId` (set on Devices → this model, not typed from
    memory here). That answer is now trustworthy enough to pre-fill — see the
    effect below `catalogModel` — but it only ever fills an EMPTY choice; the
    operator holding the device can still override it for an odd unit.
  */
  const [profileId, setProfileId] = useState<string | null>(null);
  const activeProfile = profileById(profileId);
  /*
    🔴 THE DEVICE OWNER OVERRIDE — TWO SEPARATE ANSWERS, NEVER ONE CLICK.

    `armed` only opens the consequence; `accepted` is the second, differently
    worded action that actually lets the run start. A single button would put
    "ship a till we can never wipe" one stray click away from an operator who
    is trying to dismiss a red box, and the mistake is unrecoverable without a
    factory reset at the customer's site.
  */
  const [unmanagedArmed, setUnmanagedArmed] = useState(false);
  const [unmanagedAccepted, setUnmanagedAccepted] = useState(false);
  /* Three separate answers, read off three different places on the device —
     the connect port is not the pairing port, and neither is the code. */
  const [port, setPort] = useState('');
  const [pairPort, setPairPort] = useState('');
  const [pairCode, setPairCode] = useState('');
  const connFields = connectionFields(activeProfile);
  const connError = validateConnection(activeProfile, { ip, port, pairPort, pairCode });
  const [channel, setChannel] = useState<'stable' | 'canary'>('stable');
  /*
    THE TILL'S TIME ZONE — REQUIRED, PRE-FILLED, NEVER ASSUMED.
    Seeded once from this console's own zone so the common bench case is a
    glance rather than a search. `canInstall` refuses an empty value, so a
    browser whose zone is not in the offered list leaves a visibly unanswered
    field instead of silently falling back to something plausible. Why it is
    mandatory at all is written out in `timezones.ts`.
  */
  const timeZones = useMemo(() => listTimeZones(), []);
  const [timezone, setTimezone] = useState<string>(() => detectTimeZone());
  const [reboot, setReboot] = useState(true);
  const [noBuild, setNoBuild] = useState(false);
  /*
    HOW MUCH OF THE DEVICE THIS RUN WIPES — exactly one of two, always.

    `adb install -r` keeps the app's data, so a till reinstalled without a wipe
    comes back up still holding the pairing from its previous life — the
    terminalId, tenant and location in the WebView's `posTerminalConfig` — and
    cannot be handed to a different merchant until somebody clears it by hand.
    Onboarding a device means onboarding a BLANK one, so 'factory' is the
    default and keeping the data is the deliberate exception.

    Two switches rather than one, because "off" on a single "keep the data"
    checkbox does not read as "this will erase the till" — and erasing the till
    is what it means. Naming both outcomes makes the destructive one something
    an operator chooses rather than something they fail to opt out of. They are
    mutually exclusive: turning one on turns the other off, and the one already
    on cannot be turned off, so a run can never be left with no reset mode.
  */
  const [resetMode, setResetMode] = useState<'factory' | 'soft'>('factory');
  /** What the installer takes on argv: `--keep-data` is the soft reset. */
  const keepData = resetMode === 'soft';
  /** Whether THIS run actually cleared the device, scraped rather than assumed. */
  const didResetRef = useRef(false);
  const [didReset, setDidReset] = useState(false);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState('');
  const logRef = useRef<HTMLPreElement>(null);
  const [connectedSerial, setConnectedSerial] = useState<string | null>(null);
  const [installExitCode, setInstallExitCode] = useState<number | null>(null);
  const linkedRef = useRef(false); // guards against double-firing terminal_link within one run
  const brandedRef = useRef(false);
  const versionRef = useRef(false); // guards against double-firing the version record
  /*
    WHAT THE INSTALLER SAID IT PUT ON THE DEVICE, and what it was talking to.
    Refs, not state, because the stamp is written in `install()`'s finally
    block — which reads the values THIS run produced, and the state those
    setters queue is not visible to a closure captured before them.
  */
  const installedBuildRef = useRef<string | null>(null);
  const connectedSerialRef = useRef<string | null>(null);
  /** The last probe's facts, or null when the installer never emitted a block. */
  const deviceFactsRef = useRef<DeviceFacts | null>(null);
  /*
    WHAT THE DEVICE SAYS IT IS, read off the hardware by the installer.

    The unit record on the bench carried "dfgdfgdfgdg" as an app build and
    "dfgdsfg" as an OS version, because both are free-text boxes on a form.
    Anything the device can answer for itself should not be a question put to
    a person at all — so these overwrite whatever was typed.
  */
  const identityRef = useRef<{ serial?: string; os?: string; mac?: string } | null>(null);
  // Mirrors installExitCode but readable synchronously at the end of `install()`
  // — `setInstallExitCode` doesn't update the state this closure captured, so
  // the finally block needs its own up-to-date value to decide whether the
  // run failed.
  const exitCodeRef = useRef<number | null>(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  /** A real adb hardware serial, not the `<ip>:5555` connection address. */
  const looksLikeHardwareSerial = (s: string) => !!s && !/^\d{1,3}(\.\d{1,3}){3}:\d+$/.test(s);

  const scanChunk = (chunk: string) => {
    if (!linkedRef.current) {
      const m = chunk.match(/✓ device: (\S+)/);
      if (m) {
        linkedRef.current = true;
        connectedSerialRef.current = m[1];
        setConnectedSerial(m[1]);
        if (unit) {
          recordEvent({
            kind: 'terminal_link',
            label: looksLikeHardwareSerial(m[1])
              ? `Connected — device serial ${m[1]}`
              : `Connected over Wi-Fi (${m[1]}) — installer reports the connection address, not a hardware serial`,
            source: 'system',
            meta: { connection: m[1], source, channel },
          });
        }
      }
    }
    if (!brandedRef.current) {
      const b = chunk.match(/✓ boot animation v(\S+) applied/);
      if (b && unit) {
        brandedRef.current = true;
        recordEvent({ kind: 'branding_update', label: `Boot branding applied — v${b[1]} (${channel})`, source: 'system', meta: { version: b[1], channel } });
      }
    }
    // The installer now reports exactly what it installed, so the operator never
    // types a version by hand and the record cannot drift from what is on the box.
    if (!versionRef.current) {
      const v = chunk.match(/✓ version: (\S+)/);
      if (v && unit) {
        versionRef.current = true;
        installedBuildRef.current = v[1];
        setAppBuild(v[1]);
        hardwareApi.upsertUnit({ unitId: unit.unitId, modelId: unit.modelId, appBuild: v[1] })
          .then(() => recordEvent({
            kind: 'config_saved',
            label: `Installed ${v[1]} (${source}${targetId ? ` · ${targetId}` : ''})`,
            source: 'system',
            meta: { appBuild: v[1], source, target: targetId },
          }))
          .catch((e: any) => toastService.warning(
            `Installed ${v[1]}, but couldn't record it on the unit: ${e?.message || 'unknown error'}`,
          ));
      }
    }
    /* WHAT THE MACHINE SAID ABOUT ITSELF.
       `chunk` here is the ACCUMULATED text, not one read (see the caller), so
       a block whose marker and JSON straddle a stream boundary is still whole
       by the next pass. `parseDeviceFacts` takes the LAST block, so a retry's
       facts replace the previous run's instead of merging with them — the
       earlier block describes the device before this run touched it. */
    const facts = parseDeviceFacts(chunk);
    if (facts) {
      deviceFactsRef.current = facts;
      // Shown as it is read, not only once the run ends: the management state
      // is the one fact an operator may want to abort on, and finding out
      // after the APK has landed is finding out too late.
      setLiveFacts(facts);
    }

    /* Serial, OS and MAC as the device itself reports them. */

    if (!identityRef.current) {
      const m = chunk.match(/✓ identity: serial=(\S*) os=(\S*) mac=(\S*)/);
      if (m) {
        const got = {
          serial: m[1] && m[1] !== '?' ? m[1] : undefined,
          os: m[2] && m[2] !== '?' ? m[2] : undefined,
          mac: m[3] && m[3] !== '?' ? m[3] : undefined,
        };
        identityRef.current = got;
        // Fill the form in front of the operator too, so what is about to be
        // saved is visible rather than silently posted.
        if (got.serial) setSerialNumber(got.serial);
        if (got.os) setOsVersion(got.os);
        if (got.mac) setMac(got.mac);
      }
    }

    /* The installer says whether the wipe actually took. Scraped, never
       inferred from the checkbox: `pm clear` can fail on a locked device and
       an operator who believes a terminal is blank when it is not is the
       failure this whole flag exists to prevent. */
    if (!didResetRef.current && /✓ reset: app data cleared/.test(chunk)) {
      didResetRef.current = true;
      setDidReset(true);
    }

    const exit = chunk.match(/\[exit (-?\d+)\]/);
    if (exit) {
      exitCodeRef.current = Number(exit[1]);
      setInstallExitCode(exitCodeRef.current);
    }
  };

  const install = async () => {
    if (running || !unit || managementBlocks) return;
    /*
      RECORD THE UNIT AS UNMANAGED BEFORE THE RUN, NOT AFTER.
      The probe's `deviceOwner: false` lands on the unit row with the hardware
      block at the end of a SUCCESSFUL run — but a run that fails half way
      still happened, and the decision to proceed without Device Owner was
      still taken. The timeline entry is the durable record of that decision.
    */
    if (unmanagedAccepted && management === 'unmanaged') {
      recordEvent({
        kind: 'config_saved',
        label: 'Installed WITHOUT Device Owner — unmanaged override accepted. This unit cannot be remotely restarted, locked or wiped.',
        source: 'admin',
        meta: {
          deviceOwner: false,
          unmanagedOverride: true,
          ...(facts?.deviceOwnerPackage ? { deviceOwnerPackage: facts.deviceOwnerPackage } : {}),
        },
      });
    }
    setRunning(true);
    setConnectedSerial(null);
    setInstallExitCode(null);
    linkedRef.current = false;
    brandedRef.current = false;
    versionRef.current = false;
    installedBuildRef.current = null;
    connectedSerialRef.current = null;
    didResetRef.current = false;
    setDidReset(false);
    identityRef.current = null;
    exitCodeRef.current = null;
    // Seed BOTH the displayed log and the scan buffer from this one local, and
    // never read the `log` STATE inside this run: `log` is only the value this
    // closure captured at the last render (`setLog` above does not change it
    // synchronously), so on a second Install for the same unit it still holds
    // the ENTIRE previous run's text — including its `✓ device:`/`✓ boot
    // animation…`/`✓ version:` lines. Seeding the buffer from it would replay
    // the previous run's markers straight into this run's freshly-reset guards
    // and record the last run's version as this one's.
    // The IP and port are entered apart but travel as one address, because
    // that is what `--ip` takes and what adb understands.
    const address = composeAddress(ip, port);
    const banner = `▶ Installing ${source.toUpperCase()} on ${address || 'connected device'} for unit ${unit.trackingId || unit.unitId} …\n`;
    setLog(banner);
    try {
      // Local console → the Vite middleware. Hosted console → the operator's
      // own agent, which is the only thing in the path that can reach the till.
      const endpoint = IS_LOCAL_CONSOLE ? '/__device-install' : `${agent}/device-install`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source, target: targetId, ip: address, channel, reboot, noBuild, keepData, timezone,
          /*
            🔴 DEVICE OWNER, THE ONE DECISION THAT CANNOT BE UNDONE LATER.

            `claimOwner` is the normal path and is ON unless the device has
            already told us it is managed. A fresh unit CANNOT already be
            Device Owner — `dpm` can only name a package that is installed, so
            the APK goes on first and enrolment follows in the same run. Not
            asking would ship an un-enrollable till, and the only remedy after
            that is erasing it.

            Sent only when it is actually needed: a device that already
            answered `managed` is left alone rather than re-enrolled.

            `allowUnmanaged` is the escape hatch and rides on the override the
            operator had to unlock with two separate confirmations. Both are
            real booleans — the middleware forwards neither from a truthy
            string, on purpose.
          */
          claimOwner: management !== 'managed',
          ...(unmanagedAccepted ? { allowUnmanaged: true } : {}),
          // Only sent when both halves are filled in; the server pairs first
          // and refuses to install if that fails.
          ...(pairPort.trim() && pairCode.trim()
            ? { pairAddress: composeAddress(ip, pairPort), pairCode: pairCode.trim() }
            : {}),
        }),
      });
      // 🔴 THE INSTALLER IS NOT THERE — SAY SO, DO NOT STREAM THE SPA.
      //
      // On a hosted console `/__device-install` falls through to the SPA and
      // answers `200 text/html`. `res.ok` is true and `res.body` is readable, so
      // without this check the loop below streamed index.html into the install
      // log: no device was touched, no marker matched, and the run looked like
      // it had merely produced strange output rather than having never happened.
      // Content-type is the honest probe — the real installer streams plain text.
      const ctype = res.headers?.get('content-type') || '';
      if (ctype.includes('text/html')) {
        const msg = banner
          + '\n✗ The install endpoint answered with a web page, not installer output.\n\n'
          + 'Nothing was sent to the device. This means the request reached the static site\n'
          + 'instead of an installer — on a hosted console, start the agent (npm run agent)\n'
          + 'and reload this page.\n';
        setLog(msg);
        toastService.error('No installer answered — nothing was sent to the device.');
        return;
      }
      // The agent refuses a source that does not match the console's URL. It is
      // a plain-text 403 and is the operator's answer, not a crash — show it.
      if (res.status === 403) {
        const t = await res.text();
        setLog(banner + '\n' + t);
        toastService.error('Refused — that build does not match this console.');
        return;
      }
      if (!res.body) { const t = await res.text(); const full = banner + t; setLog(full); scanChunk(full); return; }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = banner;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        acc += chunk;
        setLog(acc);
        // Scan the ACCUMULATED text, not the raw chunk: a `✓ version:` (or any
        // other scraped) line can land split across two reads, and a per-chunk
        // regex silently misses it — with no warning, and no version recorded.
        // The one-shot refs in scanChunk still stop anything double-firing.
        scanChunk(acc);
      }
    } catch (e: any) {
      setLog((l) => l + `\n[failed] ${e?.message || e}\n(This panel only works when the super-admin UI is running locally via \`npm run dev\`.)\n`);
    } finally {
      // The spec requires a failed install to be recorded on the unit's
      // timeline, same as a success — otherwise unit history is
      // systematically optimistic (only the success path ever wrote
      // anything). `appBuild` is deliberately NOT touched here: the version
      // record must only ever reflect what actually landed on the device.
      if (exitCodeRef.current !== null && exitCodeRef.current !== 0) {
        recordEvent({
          kind: 'install_failed',
          label: `Install failed — ${source}${targetId ? ` · ${targetId}` : ''} (exit ${exitCodeRef.current})`,
          source: 'system',
          meta: { source, target: targetId, exitCode: exitCodeRef.current },
        });
      }

      /*
        THE VERIFIED STAMP — what unlocks a till's provisioning checklist.

        Written HERE, not beside the `appBuild` write in `scanChunk`, and the
        difference is the whole guarantee. `✓ version:` is scraped mid-stream,
        before the run has finished; an install that reports a version and then
        fails would stamp a device that never came up. Both conditions have to
        hold: the installer said what it put on, AND the run exited clean.

        `appBuild` remains the looser, hand-editable field it always was. This
        one can only be written from a real run, which is why the gate reads it
        instead.
      */
      /*
        A CLEAN RUN THAT RECORDED NOTHING IS THE ONE FAILURE THAT LOOKED LIKE
        SUCCESS.

        A unit on the bench sat at `osInstall: null` with a hand-typed
        "dfgdfgdfgdg" as its build while its order still read "OS not
        installed" — and nothing on this screen said why, because the log
        ended in ✓ and the panel simply had nothing to write. If the installer
        finished but never told us what it put on, say so here rather than
        leaving somebody to discover it two screens away.
      */
      if (exitCodeRef.current === 0 && !installedBuildRef.current) {
        toastService.warning(
          'The install finished but never reported a version, so nothing was recorded against this device — '
          + 'its checklist stays locked. Check the log for "✓ version:".',
        );
      } else if (exitCodeRef.current === 0 && installedBuildRef.current && !unit) {
        toastService.warning('The install finished but no unit is selected, so nothing was recorded against a device.');
      }

      if (exitCodeRef.current === 0 && installedBuildRef.current && targetId && unit) {
        // `local` compiles from this working tree and publishes no artifact, so
        // there is nothing to cite — left off rather than borrowed from
        // whatever dev happens to be carrying today.
        const published = source === 'local' ? undefined : rows.find((r) => r.id === targetId)?.[source];
        hardwareApi.recordOsInstall(unit.unitId, {
          appBuild: installedBuildRef.current,
          source,
          target: targetId,
          ...(published?.versionCode !== undefined ? { versionCode: published.versionCode } : {}),
          ...(published?.hash ? { hash: published.hash } : {}),
          ...(connectedSerialRef.current ? { serial: connectedSerialRef.current } : {}),
          // What the installer REPORTED doing, not what the checkbox asked
          // for: `pm clear` can fail on a locked device, and a stamp claiming
          // a blank terminal that is not blank is worse than no claim.
          reset: didResetRef.current,
        })
          .then(async () => {
            /*
              SAVE WHAT THE DEVICE REPORTED, replacing whatever was typed.
              `osInstall` is pinned server-side, so the human-readable identity
              fields have to go through the ordinary upsert — but the VALUES
              come off the hardware, which is the part that was missing.
            */
            const id = identityRef.current;
            const hw = deviceFactsRef.current;
            if ((id && (id.serial || id.os || id.mac)) || hw) {
              try {
                await hardwareApi.upsertUnit({
                  unitId: unit.unitId,
                  modelId: unit.modelId,
                  ...(id?.serial ? { serialNumber: id.serial } : {}),
                  ...(id?.os || hw?.osVersion ? { osVersion: id?.os || hw?.osVersion } : {}),
                  ...(id?.mac || primaryMac(hw) ? { mac: id?.mac || primaryMac(hw) } : {}),
                  /* The full spec block, stamped with WHEN it was read. A
                     block with no timestamp cannot be told apart from one
                     captured against a device that has since been re-imaged
                     or swapped, which is exactly when somebody goes looking. */
                  ...(hw ? { hardware: { ...hw, capturedAt: Date.now() } } : {}),
                });
              } catch (e: any) {
                toastService.warning(`Recorded the install, but could not save the device identity: ${e?.message || 'unknown error'}`);
              }
            }
            loadUnits();
          })
          .catch((e: any) => toastService.warning(
            `Installed ${installedBuildRef.current}, but couldn't record the verified install: ${e?.message || 'unknown error'}. `
            + 'The provisioning checklist for this device stays locked until it is recorded.',
          ));
      }
      setRunning(false);
    }
  };

  const connectSucceeded = installExitCode === 0 && !!connectedSerial;

  // -- identity panel (verify + record what the connection reported) ---------
  const [mac, setMac] = useState('');
  const [osVersion, setOsVersion] = useState('');
  const [appBuild, setAppBuild] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [terminalId, setTerminalId] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  /** Drives the "Saved" line under the form, so an autosave is visible. */
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState<number | null>(null);

  // Prefill from the unit's own stored record when it's selected, and — once
  // a connect succeeds — from whatever the connection actually reported (only
  // the serial, and only when it looks like a real hardware serial; MAC/OS/
  // build are never reported by this flow, so those stay whatever was already
  // on file, or blank).
  useEffect(() => {
    if (!unit) return;
    setMac(unit.mac || '');
    setOsVersion(unit.osVersion || '');
    setAppBuild(unit.appBuild || '');
    setSerialNumber(unit.serialNumber || '');
    setTerminalId(unit.terminalId || '');
  }, [unit]);

  useEffect(() => {
    if (connectSucceeded && connectedSerial && looksLikeHardwareSerial(connectedSerial) && !unit?.serialNumber) {
      setSerialNumber(connectedSerial);
    }
  }, [connectSucceeded, connectedSerial, unit]);

  /**
   * WHAT THE PROBE READ, SHOWN AS SOON AS IT IS READ.
   *
   * Held in state rather than only on the ref so the panel re-renders — the
   * operator watches these fill in while the install is still running, which
   * is the difference between a form they have to check and a report they can
   * read. Falls back to whatever the unit already had on file.
   */
  const [liveFacts, setLiveFacts] = useState<DeviceFacts | null>(null);
  useEffect(() => { setLiveFacts(null); }, [unitId]);
  useEffect(() => {
    if (!running && deviceFactsRef.current) setLiveFacts(deviceFactsRef.current);
  }, [running, installExitCode]);

  const facts: DeviceFacts | null = liveFacts
    || (unit?.hardware as DeviceFacts | undefined)
    || null;

  /**
   * 🔴 MANAGED / NOT MANAGED / NOT YET ASKED.
   *
   * THREE states, not two, and the third is the whole point. A device we have
   * never probed — a fresh unit, or one last touched by an installer too old
   * to report the field — is `unknown`. It is NOT shown in red and it does NOT
   * block, because "we have not asked" is not evidence of anything; treating
   * absence as a negative is the same mistake this page has already shipped
   * twice.
   *
   * Only a device that ANSWERED `false` is unmanaged, and only that blocks.
   */
  const management = managementState(facts);
  /** The run is refused until the operator either fixes it or deliberately overrides. */
  const managementBlocks = management === 'unmanaged' && !unmanagedAccepted;

  // A device that comes back managed retires the override with it — consent
  // was for the state that was on the screen when it was given.
  useEffect(() => {
    if (management === 'managed') { setUnmanagedArmed(false); setUnmanagedAccepted(false); }
  }, [management]);

  /**
   * AUTOSAVE — the operator finishes typing and it is saved, with no button.
   *
   * DEBOUNCED, not per-keystroke: a save per character would be one write per
   * letter of a serial number, and every one of them appends a `config_saved`
   * event to the unit's timeline. 1.2s after they stop is "finished typing"
   * without being a wait anybody notices.
   *
   * ONLY when something actually differs from what is on file. Without that
   * test, merely SELECTING a unit writes a row and a timeline entry for a
   * device nobody touched, and the history stops meaning anything.
   *
   * Never while an install is running: that flow does its own pinned write
   * (osInstall + the hardware block) and two writers racing on one row is how
   * a verified install gets overwritten by a half-filled form.
   */
  const identityDirty = !!unit && (
    (serialNumber || '') !== (unit.serialNumber || '')
    || (mac || '') !== (unit.mac || '')
    || (osVersion || '') !== (unit.osVersion || '')
    || (appBuild || '') !== (unit.appBuild || '')
    || (terminalId || '') !== (unit.terminalId || '')
  );

  useEffect(() => {
    if (!unit || running || savingIdentity || !identityDirty) return undefined;
    const t = setTimeout(() => { saveIdentity({ silent: true }); }, 1200);
    return () => clearTimeout(t);
    // `saveIdentity` is intentionally not a dependency — it is recreated every
    // render, which would restart the timer on every keystroke and mean the
    // save never fires while anyone is typing anywhere on the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, running, savingIdentity, identityDirty, serialNumber, mac, osVersion, appBuild, terminalId]);

  const saveIdentity = async (opts: { silent?: boolean } = {}) => {
    if (!unit) return;
    setSavingIdentity(true);
    try {
      const patch: Partial<DeviceUnitRow> = {
        unitId: unit.unitId,
        modelId: unit.modelId,
        mac: mac || undefined,
        osVersion: osVersion || undefined,
        appBuild: appBuild || undefined,
        serialNumber: serialNumber || undefined,
        terminalId: terminalId || undefined,
      };
      await hardwareApi.upsertUnit(patch);
      // `os_installed`/`build_installed` (or the `*_changed` variants) are
      // fired automatically by the backend when osVersion/appBuild actually
      // change (see b1-lifecycle-report.md B2) — no duplicate manual event
      // for those. This one covers the save action itself (e.g. MAC/terminal
      // link, or an unchanged re-save), so the timeline reads completely.
      await recordEvent({
        kind: 'config_saved',
        label: `Configuration saved${terminalId ? ` — linked to terminal ${terminalId}` : ''}`,
        source: 'admin',
        meta: { mac: mac || undefined, osVersion: osVersion || undefined, appBuild: appBuild || undefined, terminalId: terminalId || undefined },
      });
      // An AUTOSAVE says so quietly — a toast per pause in typing is noise.
      // A FAILURE is never quiet, whichever way the save was triggered: the
      // operator has to know the thing they typed did not stick.
      if (!opts.silent) toastService.success('Unit configuration saved.');
      setLastAutoSaveAt(Date.now());
      loadUnits();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to save unit configuration');
    } finally {
      setSavingIdentity(false);
    }
  };

  // -- quick actions (things this flow doesn't have a real button for yet) ---
  // `datetime_set` is listed as a valid timeline kind, but nothing in this
  // installer actually sets the device clock — same honesty rule as
  // restart/first-test-transaction: log it as a manual, one-tap RECORD of
  // something the operator did by hand, never as something this panel did.
  const QUICK_ACTIONS: { kind: string; label: string; recordLabel: string }[] = [
    { kind: 'restart', label: 'Restarted the device', recordLabel: 'Device restarted' },
    { kind: 'datetime_set', label: 'Set date & time on the device', recordLabel: 'Device date/time set' },
    { kind: 'first_test_transaction', label: 'Ran the first test transaction', recordLabel: 'First test transaction completed' },
  ];
  const [recordingKind, setRecordingKind] = useState<string | null>(null);
  const recordQuickAction = async (kind: string, recordLabel: string) => {
    setRecordingKind(kind);
    try {
      await recordEvent({ kind, label: recordLabel, source: 'admin' });
      toastService.success('Recorded.');
    } finally {
      setRecordingKind(null);
    }
  };

  /**
   * WHAT THIS MODEL SAYS TO INSTALL. Absent means nobody has answered for this
   * device yet — which is a REFUSAL, not a free choice: an install panel that
   * falls back to "pick anything" on a missing answer is the same coin toss
   * this change exists to remove.
   */
  const catalogTarget = (unit && models.find((m) => m.modelId === unit.modelId)?.buildTarget) || '';
  const catalogModel = (unit && models.find((m) => m.modelId === unit.modelId)) || null;
  // The catalog model says which hardware its units are. Re-derive the choice
  // from the CURRENT unit's model whenever the unit (or that model's profile)
  // changes — one effect, so it can never read the previous unit's pick. A
  // manual pick for an odd unit survives until the unit or its model's
  // profile changes; an unknown/absent profile leaves it empty for a person.
  const catalogProfileId = (catalogModel as any)?.appProfile?.installProfileId as string | undefined;
  useEffect(() => {
    setProfileId(catalogInstallProfileId(catalogProfileId));
  }, [unitId, catalogProfileId]);
  /** The override only counts once the code has been accepted for THIS unit. */
  const targetId = (overrideUnlocked && buildOverride) || catalogTarget;
  const overridden = !!(overrideUnlocked && buildOverride && buildOverride !== catalogTarget);

  const selectedRow = rows.find((r) => r.id === targetId) || null;

  const sourceBtn = (v: 'local' | 'dev' | 'prod', label: string) => {
    const available = isEnvAvailable(selectedRow, v);
    return (
      <button
        key={v}
        onClick={() => setSource(v)}
        disabled={!available}
        title={available ? undefined : 'No build published for this target in this environment'}
        className={`px-4 py-2 rounded-lg text-sm font-extrabold border disabled:opacity-40 disabled:cursor-not-allowed ${
          source === v
            ? v === 'prod' ? 'bg-red-500/15 border-red-400 text-red-600' : 'bg-emerald-500/15 border-emerald-500 text-emerald-600'
            : 'border-gray-300 text-gray-500 hover:border-gray-400'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <>
    <div className="space-y-4">
      {/* STEP 1 — pick the physical unit */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-3">
        <div>
          <h2 className="text-sm font-extrabold text-gray-900">① Pick the unit</h2>
          <p className="text-xs text-gray-500 mt-0.5">Search by tracking ID, serial or model — checked-in stock, allocated or already mid-provisioning.</p>
        </div>
        {/* Cards, not a dropdown: choosing which box a customer receives is not
            a form field. The picture, the serial and the printed barcode are
            the only things that identify the device in the operator's hand,
            and a one-line label shows none of them. */}
        <UnitPickerCards
          units={units}
          models={models}
          value={unitId}
          loading={unitsLoading}
          onChange={(v) => selectUnit(v)}
          onSwapped={loadUnits}
        />
      </div>

      {/* STEP 2 — connect / pair, install, apply branding */}
      <div className={`rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-3 ${!unit ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <h2 className="text-sm font-extrabold text-gray-900">② Connect &amp; install</h2>
          <p className="text-xs text-gray-500 mt-0.5">Pick the build and where it comes from. <strong>Dev</strong> and <strong>Production</strong> install the APK CI published — this computer needs only adb. <strong>Local</strong> builds this working tree and is for developers.</p>
        </div>

        {/*
          THE INSTALL STEP IS LOCAL, AND THE OPERATOR HAS TO KNOW BEFORE THEY
          PRESS IT. adb runs on the machine serving this page and has to reach
          the till's IP; a CloudFront-hosted console can do neither. It used to
          fail invisibly — index.html streamed into the log — so the notice goes
          above the controls, not in the output after the fact.
        */}
        {!IS_LOCAL_CONSOLE && agent === null && (
          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
            <p className="text-[11px] text-stone-600">Looking for the local install agent…</p>
          </div>
        )}
        {!IS_LOCAL_CONSOLE && agent === false && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-[11px] text-amber-900">
              <strong>Start the install agent to install from here.</strong> adb has to run on a machine that can
              reach the till on your network, which this hosted console cannot do by itself. The agent is that
              machine&rsquo;s half — everything else, including this unit&rsquo;s record and timestamps, is written
              against {source === 'prod' ? 'production' : 'dev'} by this page.
            </p>
            <pre className="mt-1.5 overflow-x-auto rounded bg-white/70 px-2 py-1 text-[11px] text-stone-800">
              cd applications/super-admin-ui{'\n'}npm run agent
            </pre>
            <p className="mt-1 text-[11px] text-amber-900">Leave it running, then reload this page.</p>
          </div>
        )}
        {!IS_LOCAL_CONSOLE && typeof agent === 'string' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <p className="text-[11px] text-emerald-900">
              <strong>Install agent connected.</strong> Installing {source === 'prod' ? 'Production' : 'Dev'} builds
              from this console; the agent runs adb on your machine and refuses any build that does not match this
              address.
            </p>
          </div>
        )}

        {/* BUILD — stated, not asked. See the `buildOverride` comment above. */}
        <div>
          <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Build</div>

          {targetId ? (
            <div className={`rounded-lg border px-3 py-2 ${overridden ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <p className="text-sm font-extrabold text-gray-900">
                {rows.find((r) => r.id === targetId)?.displayName || targetId}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-600">
                {overridden ? (
                  <>
                    <strong className="text-amber-800">Overridden.</strong> This model is configured
                    for <strong>{rows.find((r) => r.id === catalogTarget)?.displayName || catalogTarget || 'no build'}</strong>.
                  </>
                ) : (
                  <>
                    From the catalog — <strong>{catalogModel?.modelName || 'this model'}</strong> installs
                    this build. Change it on the device, not here, and every unit follows.
                  </>
                )}
              </p>
            </div>
          ) : (
            /* NO ANSWER IS A STOP, NOT A PROMPT. Falling back to a free pick
               here would reintroduce exactly the coin toss this replaced — and
               would do it on the devices nobody has configured, which are the
               ones most likely to be a model somebody has never installed
               before. */
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
              <p className="text-[12px] font-bold text-amber-900">
                {unit ? 'This model has no build configured.' : 'Pick a unit first.'}
              </p>
              {unit && (
                <p className="mt-0.5 text-[11px] text-amber-800">
                  Set it once on{' '}
                  <a
                    className="underline font-semibold"
                    href={`/inventory/stock/${encodeURIComponent(unit.modelId || '')}/edit`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {catalogModel?.modelName || unit.modelId}
                  </a>
                  {' '}and every unit of it installs the right build from then on.
                </p>
              )}
            </div>
          )}

          {/* THE DELIBERATE EXCEPTION. Same wall as changing which device a
              customer gets: a code, emailed to whoever asked, super admin
              copied, server-enforced. */}
          {unit && (
            overrideUnlocked ? (
              <div className="mt-2">
                <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">
                  Install a different build
                </div>
                <SearchableDropdown
                  options={rows.map((r) => ({
                    value: r.id,
                    label: r.displayName,
                    subtitle: `${r.dev ? `dev ✓ ${(r.dev.version || '').slice(0, 7)}` : 'dev —'} · ${r.prod ? `prod ✓ ${(r.prod.version || '').slice(0, 7)}` : 'prod —'}`,
                  }))}
                  value={buildOverride || null}
                  onChange={(v) => setBuildOverride(v || '')}
                  placeholder={rowsLoading ? 'Loading published builds…' : rows.length ? 'Select a build…' : 'No builds published yet'}
                  searchPlaceholder="Build name…"
                  disabled={rowsLoading || rows.length === 0}
                />
              </div>
            ) : (
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] text-gray-500 hover:text-gray-700">
                  Install a different build…
                </summary>
                <div className="mt-2">
                  <OverrideGate
                    unitId={unit.unitId}
                    intent="install a build this model is not configured for"
                    onUnlocked={() => setOverrideUnlocked(true)}
                  />
                </div>
              </details>
            )
          )}

          {rowsError && !rowsLoading && (
            <p className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
              Couldn't load the full build list — this may be out of date. Check your connection and reload; an empty list here can mean "offline", not "nothing published".
            </p>
          )}
        </div>

        <div>
          <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Source</div>
          <div className="flex gap-2">
            {sources.includes('local') && sourceBtn('local', 'Local')}
            {sources.includes('dev') && sourceBtn('dev', 'Dev latest')}
            {sources.includes('prod') && sourceBtn('prod', 'Production')}
          </div>
          <p className="mt-1.5 max-w-md text-[11px] text-gray-500">
            Local, Dev and Production are signed with three different keys. Switching sources on a device already
            set up from a different one will fail to install and may require a factory reset to recover.
            {!IS_LOCAL_CONSOLE && (
              <> This console installs <strong>{source === 'prod' ? 'Production' : 'Dev'}</strong> builds only — the
              environment follows the address you opened it on, so the signing key can never be the wrong one.</>
            )}
          </p>
        </div>

        {/*
          PROFILE FIRST, THEN WHAT IT ASKS FOR. The hardware decides what a
          valid address even looks like: the K10 takes a bare IP on :5555, the
          Vector needs <ip>:<random port> from Wireless debugging and will never
          connect from an IP alone. Asking for the address first is asking a
          question whose right answer depends on the next one — so the address
          box does not appear until the hardware is named.
        */}
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Device profile</div>
          <div className="w-80">
            <SearchableDropdown
              value={profileId}
              onChange={(v) => setProfileId(v)}
              options={DEVICE_PROFILES.map((p) => ({ value: p.id, label: p.displayName }))}
              placeholder="Select the hardware…"
              searchPlaceholder="Search hardware…"
            />
          </div>
          {!activeProfile ? (
            /* REQUIRED, and said as a refusal rather than a hint — the profile
               is what lets this panel check Device Owner and the peripherals
               at all. Reversed from "no profile is not an error" by the
               product owner on 2026-09-24; see validateConnection. */
            <p className="mt-2 max-w-lg rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-ruby">
              {connError}
            </p>
          ) : (
            <div className="mt-3 border-t border-gray-200 pt-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">IP address</div>
                  <input
                    value={ip}
                    onChange={(e) => setIp(e.target.value)}
                    placeholder="192.168.1.50"
                    className="w-44 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 outline-none"
                  />
                </div>
                <div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">
                    Port {connFields?.portRequired
                      ? <span className="text-ruby">required</span>
                      : <span className="normal-case tracking-normal">(optional)</span>}
                  </div>
                  <input
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder={connFields?.portPlaceholder}
                    className="w-28 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 outline-none"
                  />
                </div>
              </div>
              <p className="mt-1.5 max-w-lg text-[11px] text-gray-500">{connFields?.help}</p>

              {/*
                PAIRING, only where the hardware has it, and separate from the
                connection. The pairing port is NOT the connect port and the
                code is single-use — both are read off the device's own
                "Pair device with pairing code" dialog while it is open.
                Left blank on a unit that is already paired.
              */}
              {connFields?.pairing && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-white p-2.5">
                  <div className="text-[11px] font-semibold text-gray-700">
                    First-time pairing <span className="font-normal text-gray-500">— skip if this unit is already paired</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-end gap-3">
                    {/*
                      THE IP IS SHOWN, NOT ASKED FOR AGAIN.

                      The device's "Pair device with pairing code" dialog prints
                      ONE string — `192.168.86.114:43927` — and the Wireless
                      debugging screen above it prints another that looks
                      identical but ends in a different port. Asking for a bare
                      port here made the operator pull those two apart in their
                      head, and picking the wrong one fails as an adb timeout
                      rather than a sentence.

                      Echoing the IP makes the field the same SHAPE as what is
                      on the screen they are reading from, so the only thing
                      left to do is copy the digits after the colon. It is not a
                      second input because there is no second IP: the request
                      already sends composeAddress(ip, pairPort), so this is
                      showing what will be used, not collecting it.
                    */}
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">
                        Pairing address <span className="normal-case tracking-normal text-gray-400">(same IP, different port)</span>
                      </div>
                      <div className="flex items-stretch">
                        <span
                          className={`flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-sm tabular-nums ${
                            ip.trim() ? 'text-gray-600' : 'text-gray-400 italic'
                          }`}
                        >
                          {ip.trim() || 'enter the IP above'}
                        </span>
                        <span className="flex items-center border-y border-gray-300 bg-gray-50 text-sm text-gray-400 select-none">:</span>
                        <input
                          value={pairPort}
                          onChange={(e) => setPairPort(e.target.value)}
                          inputMode="numeric"
                          placeholder="42489"
                          aria-label="Pairing port"
                          className="w-28 px-3 py-2 rounded-r-lg bg-white border border-l-0 border-gray-300 text-sm text-gray-900 placeholder-gray-400 tabular-nums focus:border-ruby outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Pairing code</div>
                      <input
                        value={pairCode}
                        onChange={(e) => setPairCode(e.target.value)}
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="585271"
                        className="w-28 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 outline-none"
                      />
                    </div>
                  </div>
                  <p className="mt-1.5 max-w-lg text-[11px] text-gray-500">
                    Developer options → Wireless debugging → <b>Pair device with pairing code</b>. That dialog
                    shows its own <b>IP address &amp; Port</b> — same IP, but a <b>different port</b> from the
                    one above. Copy just the digits after the colon. Both the port and the code change every
                    time the dialog is reopened.
                  </p>
                </div>
              )}

              {connError && (
                <p className="mt-2 max-w-lg text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  {connError}
                </p>
              )}
              {!activeProfile.bootBrandingOverAdb && (
                <p className="mt-1.5 max-w-lg text-[11px] text-gray-500">{activeProfile.notes}</p>
              )}
            </div>
          )}
        </div>

        {/*
          🔴 DEVICE OWNER — THE LAST PLACE THIS MISTAKE CAN STILL BE CAUGHT.

          Device Owner can only be claimed on a factory-reset device with no
          accounts that has not finished setup, and can only be removed by
          another factory reset. A till that goes out without it can never be
          remotely restarted, locked down or wiped, and cannot be enrolled
          later without erasing a machine that is by then in a shop.

          So it is stated at full size beside the Install button rather than
          listed among the specs below, and `false` refuses the run.
        */}
        <div
          className={`mt-4 rounded-2xl border p-3 ${
            management === 'managed' ? 'border-emerald-200 bg-emerald-50'
              : management === 'unmanaged' ? 'border-ruby bg-rose-50'
                : 'border-stone-200 bg-stone-50'
          }`}
        >
          {/*
            THE BRAND MARK ON THE ONE CARD THAT DECIDES THE DEVICE'S WHOLE LIFE.
            This is the till the merchant will look at every day, and enrolment
            is the irreversible half of setting it up — so the card is signed.

            The `-dark-` variant is deliberate and is NOT a theme bug: in this
            pack, "light"/"dark" name the BACKGROUND the mark is drawn for, not
            the ink. `-light-` sets "Ruby" in white for dark surfaces, which on
            super-admin's ruby-on-light theme renders as a gem and the word
            "Till" with a blank gap where "Ruby" should be. `-light-` is shipped
            alongside it for the dark surfaces (device wallpaper, boot) that do
            need it.
          */}
          <img
            src="/brand/rubytill-horizontal-transparent-dark-1024w.png"
            alt="Ruby Till"
            className="mb-2.5 h-5 w-auto select-none"
            draggable={false}
          />
          <div className="flex items-start gap-2.5">
            {management === 'managed' ? <ShieldCheck size={20} className="mt-px shrink-0 text-emerald-600" />
              : management === 'unmanaged' ? <ShieldAlert size={20} className="mt-px shrink-0 text-ruby" />
                : <ShieldQuestion size={20} className="mt-px shrink-0 text-stone-400" />}
            <div className="min-w-0">
              <p className={`text-sm font-extrabold ${
                management === 'managed' ? 'text-emerald-700'
                  : management === 'unmanaged' ? 'text-ruby' : 'text-stone-500'
              }`}>
                {management === 'managed' ? 'Managed — Device Owner'
                  : management === 'unmanaged' ? 'NOT managed — no Device Owner'
                    : 'Device Owner not checked yet'}
              </p>

              {management === 'managed' && (
                <p className="mt-0.5 text-[11px] text-emerald-800">
                  Remote restart, lockdown and wipe will work on this unit.
                  {facts?.deviceOwnerPackage && (
                    <> Owner: <span className="font-mono">{facts.deviceOwnerPackage}</span>.</>
                  )}
                </p>
              )}

              {/* NOT PROBED IS NOT A FAULT. A device nobody has asked is grey and
                  says so — it is never coloured as "NOT managed", which is a
                  claim we have no evidence for. */}
              {management === 'unknown' && (
                <p className="mt-0.5 max-w-lg text-[11px] text-stone-500">
                  This device has not been probed, so we do not know either way — not the same as unmanaged.
                  The installer reports it on the next run.
                </p>
              )}

              {management === 'unmanaged' && (
                <>
                  <p className="mt-0.5 max-w-lg text-[11px] font-semibold text-ruby">
                    This till could never be restarted, locked or wiped remotely, and cannot be enrolled later without erasing it.
                  </p>
                  {/* SHORT AND SPECIFIC. Three lines, in the order they are done. */}
                  <ol className="mt-1.5 max-w-lg list-decimal space-y-0.5 pl-4 text-[11px] text-stone-700">
                    <li>Factory reset the device — Device Owner needs no accounts and an unfinished setup.</li>
                    <li>Do not sign in to any account during setup.</li>
                    <li>Run the installer again with <span className="font-mono font-semibold">--claim-owner</span>.</li>
                  </ol>

                  {/* THE OVERRIDE — TWO ACTIONS, DIFFERENTLY WORDED, AND NEVER
                      THE EASY PATH. It is a plain text button under the remedy,
                      not a button beside "Install". */}
                  <div className="mt-2.5 border-t border-rose-200 pt-2">
                    {unmanagedAccepted ? (
                      <p className="text-[11px] font-bold text-ruby">
                        Override accepted — this unit will be recorded as unmanaged.
                      </p>
                    ) : !unmanagedArmed ? (
                      <button
                        type="button"
                        onClick={() => setUnmanagedArmed(true)}
                        className="min-h-[44px] text-[11px] font-semibold text-stone-500 underline underline-offset-2 hover:text-stone-700"
                      >
                        Install unmanaged anyway
                      </button>
                    ) : (
                      <div>
                        <p className="max-w-lg text-[11px] font-semibold text-stone-800">
                          Confirm: ship this unit permanently unmanaged. Undoing it means a factory reset wherever the till ends up.
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setUnmanagedAccepted(true)}
                            className="min-h-[44px] rounded-lg bg-ruby px-4 py-2 text-xs font-extrabold text-white"
                          >
                            Yes — record this unit as unmanaged
                          </button>
                          <button
                            type="button"
                            onClick={() => setUnmanagedArmed(false)}
                            className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-600"
                          >
                            Keep it blocked
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            /* An incomplete connection is a run that cannot succeed, so it is
               refused here with a sentence rather than at an adb timeout. An
               unmanaged device is a run that must not succeed, so it is
               refused here too. */
            /* A hosted console with no agent cannot install, so the button says
               so by being disabled rather than by failing after the press. */
            disabled={running || !!connError || managementBlocks
              || (!IS_LOCAL_CONSOLE && typeof agent !== 'string')
              || !canInstall({ unitId: unit?.unitId || '', targetId, source, rows, timezone })}
            onClick={() => (source === 'prod' ? setConfirmProd(true) : install())}
            className="px-5 py-2 rounded-lg text-sm font-extrabold bg-emerald-500 text-emerald-950 disabled:opacity-50"
          >
            {running ? 'Installing…' : (isReinstall(unit) ? 'Reinstall RubyTill' : 'Install RubyTill')}
          </button>
        </div>

        {/* Say plainly that this device already carries a build, and what the
            run will replace. A re-flash on the bench is routine; doing it
            without realising the device was already configured is not. */}
        {isReinstall(unit) && !running && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              This device already runs <b className="font-mono">{unit!.osInstall!.appBuild}</b>
              {' '}(installed {new Date(unit!.osInstall!.installedAt).toLocaleDateString()}).
              Re-installing replaces it and records a new verified install.
              {unit!.state === 'packing' && ' This unit is already being packed for dispatch.'}
            </div>
          </div>
        )}

        {/* RESET MODE — one of two, never neither, never both. Turning one on
            turns the other off; turning the live one off is a no-op, because
            "no reset mode" is not a state the installer has. */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">Reset mode</div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <OptionToggle
              on={resetMode === 'factory'}
              onChange={(v) => v && setResetMode('factory')}
              tone="danger"
              label="Factory Reset"
              hint="Erases the pairing, app data and WebView storage. The till comes back blank and will take any provisioning code."
            />
            <OptionToggle
              on={resetMode === 'soft'}
              onChange={(v) => v && setResetMode('soft')}
              label="Device Soft Reset"
              hint="Replaces the app only. The existing pairing, tenant and location stay on the device."
            />
          </div>
        </div>

        {/* TIME ZONE — REQUIRED, AND DELIBERATELY NOT A TOGGLE ROW.
            It sits in its own card beside Reset mode, because it is a decision
            about the till rather than an option on the run, and because the
            operator has to actually read the clock line below it against the
            room they are standing in. A till installed on the wrong zone writes
            wrong shift boundaries and wrong business days from its first sale
            and looks entirely healthy doing it — see `timezones.ts`. */}
        <div className={`rounded-xl border p-3 ${timezone ? 'border-gray-200 bg-gray-50/70' : 'border-ruby/40 bg-ruby-tint'}`}>
          <div className="mb-2 flex items-center gap-1.5">
            <Clock3 size={13} className={timezone ? 'text-gray-400' : 'text-ruby'} />
            <span className={`text-[11px] font-bold uppercase tracking-wide ${timezone ? 'text-gray-400' : 'text-ruby'}`}>
              Time zone · required
            </span>
          </div>
          <div className="max-w-sm">
            <SearchableDropdown
              options={timeZones.map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }))}
              value={timezone || null}
              onChange={(v) => setTimezone(v || '')}
              placeholder="Choose the store's time zone…"
              searchPlaceholder="Search time zones…"
              variant="light"
            />
          </div>
          {timezone ? (
            <p className="mt-2 text-[11px] font-semibold text-stone-600">
              The till's clock will read{' '}
              <span className="font-mono font-bold text-stone-800">{describeTimeZone(timezone)}</span>.
              {' '}Check that against the store before installing.
            </p>
          ) : (
            <p className="mt-2 max-w-lg text-[11px] font-semibold text-ruby">
              Pick the zone the STORE is in, not the bench. Android tills ship on a factory zone —
              one reached a live counter twelve hours out and stamped half a day of sales onto the
              wrong date. Nothing downstream can tell those rows from good ones afterwards.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-x-8 gap-y-3">
          <OptionToggle
            on={channel === 'canary'}
            onChange={(v) => setChannel(v ? 'canary' : 'stable')}
            label="Prerelease branding"
            hint="Use the canary manifest — branding that has not been promoted to the fleet yet."
          />
          <OptionToggle
            on={reboot}
            onChange={setReboot}
            label="Reboot after install"
            hint="Required to see a new boot animation — it only plays on a cold start."
          />
          {source === 'local' && (
            <OptionToggle
              on={noBuild}
              onChange={setNoBuild}
              label="Skip rebuild"
              hint="Faster — installs the APK already in the build output instead of rebuilding."
            />
          )}
        </div>

        {log && (
          <pre ref={logRef} className="max-h-64 overflow-auto rounded-lg bg-gray-900 border border-gray-700 p-3 text-[11px] leading-relaxed text-gray-100 whitespace-pre-wrap">{log}</pre>
        )}

        {connectSucceeded && (
          <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 size={14} /> Connected &amp; installed — verify the identity fields below.
          </div>
        )}
      </div>

      {/* STEP 3 — verify + record identity */}
      <div className={`rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-3 ${!unit ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <h2 className="text-sm font-extrabold text-gray-900">③ Verify &amp; record</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            The installer reads the device over adb on every connection — model, OS, processor,
            RAM, storage, MAC addresses, displays and attached USB peripherals are captured and
            saved by themselves. <strong>Serial number</strong> is the one thing you fill in,
            because the number that matters is the one printed on the box and no probe can see
            it. Nothing here is guessed, and a field that was not read shows a dash rather than
            a plausible blank.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* SERIAL IS THE ONE FIELD A PERSON STILL OWNS.
              Android reports `ro.serialno`, but plenty of devices return a
              placeholder for it, and the number that matters commercially is
              the one printed on the box and on the asset label — which no
              probe can see. So it is prefilled from the device and stays
              editable. Everything beside it is read off the hardware, and a
              field a person can overtype is a field that will eventually hold
              "dfgdfgdfgdg", which is what the unit record actually carried. */}
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-1">Serial number</label>
            <input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)}
              placeholder={connectedSerial && looksLikeHardwareSerial(connectedSerial) ? undefined : 'Not reported — enter if known'}
              className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 outline-none" />
            <p className="mt-1 text-[10.5px] text-gray-400">The only field here you fill in.</p>
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-1">Terminal ID (optional — links this unit to a POS terminal)</label>
            <input value={terminalId} onChange={(e) => setTerminalId(e.target.value)} placeholder="Leave blank if this unit isn't a POS terminal"
              className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 outline-none" />
          </div>
        </div>

        {/* READ OFF THE HARDWARE — a report, not a form.
            These were text boxes, and a text box is a promise that the value
            is yours to set. It also means the value can be wrong: this unit
            carried "dfgdfgdfgdg" as an app build and "dfgdsfg" as an OS
            version because somebody had to type them. Anything the device can
            answer for itself is not a question. */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Read from the device
            </p>
            <p className="text-[10.5px] text-gray-400">
              {facts
                ? `Captured ${unit?.hardware?.capturedAt && !liveFacts ? `on ${new Date(unit.hardware.capturedAt).toLocaleString()}` : 'on this connection'}`
                : 'Nothing read yet — run an install to capture it'}
            </p>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 md:grid-cols-3">
            {([
              ['Model', facts?.model],
              ['Brand', facts?.brand],
              ['OS version', facts?.osVersion ? `${facts.osVersion}${facts.sdkInt ? ` (API ${facts.sdkInt})` : ''}` : undefined],
              ['App build', appBuild || undefined],
              ['Processor', facts?.cpuModel],
              ['Cores / speed', facts?.cpuCores ? `${facts.cpuCores} × ${formatMhz(facts.cpuMaxMhz)}` : undefined],
              ['Architecture', facts?.cpuAbi],
              ['RAM', facts?.ramMb ? formatMb(facts.ramMb) : undefined],
              ['Storage', facts?.storageTotalMb
                ? `${formatMb(facts.storageFreeMb)} free of ${formatMb(facts.storageTotalMb)}`
                : undefined],
              ['Wi-Fi MAC', facts?.wifiMac || (facts ? undefined : mac || undefined)],
              ['Ethernet MAC', facts?.ethernetMac],
              ['Displays', facts?.displayCount ? String(facts.displayCount) : undefined],
              ['Build fingerprint', facts?.fingerprint],
              // Unknown falls through to the dash below, same as every other
              // field nobody has read — never to a confident "NOT set".
              ['Device Owner', management === 'managed' ? (facts?.deviceOwnerPackage || 'set')
                : management === 'unmanaged' ? 'NOT set' : undefined],
            ] as [string, string | undefined][]).map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wide text-gray-400">{label}</dt>
                {/* A DASH, NOT A BLANK. "Not read on this connection" and
                    "the device has none" look identical if the row is simply
                    missing, and the first is the common one. */}
                <dd className={`truncate font-mono text-[12px] ${value ? 'text-gray-900' : 'text-gray-300'}`}
                  title={value || 'Not reported on this connection'}>
                  {value || '—'}
                </dd>
              </div>
            ))}
          </dl>

          {/* ATTACHED PERIPHERALS — a snapshot of one moment on a bench, not an
              inventory. A printer powered off during the probe is simply not
              listed, and saying so stops the list being read as a fault. */}
          {facts?.usbPeripherals?.length ? (
            <div className="mt-3 border-t border-gray-200 pt-2">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">
                USB peripherals seen when this was captured
                {hasKnownPrinter(facts) && (
                  <span className="ml-1.5 rounded bg-emerald-50 px-1 py-0.5 font-semibold text-emerald-700">
                    printer this app can drive
                  </span>
                )}
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {facts.usbPeripherals.map((u) => (
                  <span key={u} className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10.5px] text-gray-700">
                    {describeUsb(u)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/*
            EXPECTED vs SEEN — the question the raw id list cannot answer.

            The chips above say what was on the bus. They do not say whether
            that is what this machine is SUPPOSED to have, and the answer
            differs by hardware in a way nobody should be remembering: no
            printer id on a K10 is correct (it is sealed inside), no printer id
            on an A10 is a unit going out the door with the hub unplugged.
            Same absence, opposite verdicts.

            Matched against the profile the HARDWARE reported, falling back to
            whatever the operator picked. The device's own model string cannot
            be typed in wrong; the dropdown can.
          */}
          {facts?.usbPeripherals?.length ? (() => {
            const profile = profileForModel(facts.model) || activeProfile;
            const checks = checkPeripherals(profile, facts.usbPeripherals);
            if (!checks.length) return null;
            const missing = checks.filter((c) => c.status === 'missing');
            const substituted = checks.filter((c) => c.status === 'substituted');
            return (
              <div className="mt-3 border-t border-gray-200 pt-2">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">
                  Expected on {profile!.displayName}
                  {missing.length > 0 && (
                    <span className="ml-1.5 rounded bg-rose-50 px-1 py-0.5 font-semibold text-rose-700">
                      {missing.length} not found
                    </span>
                  )}
                  {substituted.length > 0 && (
                    <span className="ml-1.5 rounded bg-amber-50 px-1 py-0.5 font-semibold text-amber-700">
                      {substituted.length} possibly third-party
                    </span>
                  )}
                </p>
                {/* AMBER IS NOT A FAULT. Both tills take any USB printer and any
                    USB wedge scanner, so an unrecognised id on the bus is an
                    ordinary outcome — the installer captures lsusb ids only, with
                    no interface descriptor, so super-admin cannot name the device
                    the way the till itself can. Say that plainly instead of
                    sending someone to re-seat a working cable. */}
                {substituted.length > 0 && (
                  <p className="mt-1 max-w-lg text-[11px] text-amber-700">
                    The expected model is not on the bus, but something unrecognised is —
                    likely a third-party unit, which these tills support. The till itself
                    identifies it correctly from its USB class; only this panel cannot.
                  </p>
                )}
                {/* Two missing devices that share a hub is a CABLE, not two
                    faults. Saying so here saves an installer swapping a
                    perfectly good printer. */}
                {missing.length > 1 && (
                  <p className="mt-1 max-w-lg text-[11px] text-rose-700">
                    More than one peripheral is missing. On this hardware they share a
                    removable hub — check that hub’s cable before replacing anything.
                  </p>
                )}
                <ul className="mt-1 space-y-1">
                  {checks.map((c) => (
                    <li key={c.kind} className="flex items-start gap-1.5 text-[11.5px]">
                      <span
                        aria-hidden
                        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                          c.status === 'present' ? 'bg-emerald-500'
                            : c.status === 'missing' ? 'bg-rose-500'
                              : c.status === 'substituted' ? 'bg-amber-500' : 'bg-gray-300'
                        }`}
                      />
                      <span className={
                        c.status === 'missing' ? 'text-rose-700'
                          : c.status === 'substituted' ? 'text-amber-700' : 'text-gray-700'
                      }>
                        {c.label}
                        {c.status === 'substituted' && (
                          <span className="ml-1 text-amber-600">
                            — not the expected model; possibly {c.candidates?.map(describeUsb).join(', ')}
                          </span>
                        )}
                        {c.status === 'not-applicable' && (
                          <span className="ml-1 text-gray-400">
                            — {c.attach === 'internal' ? 'built in, never on the bus' : 'optional, not fitted'}
                          </span>
                        )}
                        {c.notes && <span className="block text-[10.5px] text-gray-400">{c.notes}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })() : null}
        </div>

        {/* THE BUTTON STAYS, and is not the only way to save.
            Typing is saved on its own a moment after it stops — but a form
            that saves invisibly leaves an operator with no way to KNOW it did,
            so the state of the save is said out loud beside the button, and
            the button remains for anyone who wants to press something. */}
        <div className="flex items-center gap-3">
          <button
            disabled={!unit || savingIdentity || !identityDirty}
            onClick={() => saveIdentity()}
            className="px-5 py-2 rounded-lg text-sm font-extrabold bg-emerald-500 text-emerald-950 disabled:opacity-50"
          >
            {savingIdentity ? 'Saving…' : identityDirty ? 'Save now' : 'Saved'}
          </button>
          <p className="text-[11px] text-gray-500">
            {savingIdentity
              ? 'Saving…'
              : identityDirty
                ? 'Saves by itself a moment after you stop typing.'
                : lastAutoSaveAt
                  ? `Saved at ${new Date(lastAutoSaveAt).toLocaleTimeString()}.`
                  : 'Everything on this panel is up to date.'}
          </p>
        </div>
      </div>

      {/* STEP 4 — quick actions + history */}
      <div className={`rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-3 ${!unit ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <h2 className="text-sm font-extrabold text-gray-900">④ Record what happened next</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            These don't exist as real actions in this app yet — pressing one only LOGS that you did it by hand, it never performs the action itself.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.kind}
              disabled={!unit || recordingKind === a.kind}
              onClick={() => recordQuickAction(a.kind, a.recordLabel)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {recordingKind === a.kind ? <RubyGemMark size={12} drawing color="currentColor" /> : (a.kind === 'restart' ? <RotateCcw size={12} /> : a.kind === 'datetime_set' ? <Clock3 size={12} /> : <Receipt size={12} />)}
              {a.label}
            </button>
          ))}
        </div>

        {unit && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1.5">Recent history</div>
            {timeline.length === 0 ? (
              <p className="text-xs text-gray-400">No events recorded yet.</p>
            ) : (
              <ul className="space-y-1">
                {timeline.slice(0, 8).map((ev, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-center gap-2">
                    <span className="text-gray-400 font-mono text-[10px] shrink-0">{new Date(ev.at).toLocaleString()}</span>
                    <span className="truncate">{ev.label}</span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-400 shrink-0">{ev.source}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>

    {confirmProd && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
          <h3 className="text-sm font-extrabold text-gray-900">Install a production build</h3>
          <p className="mt-1 text-xs text-gray-600">
            This installs the live production app on {unit?.trackingId || 'this unit'}. Type <strong>DEPLOY</strong> to confirm.
          </p>
          <input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DEPLOY"
            className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-500"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => { setConfirmProd(false); setConfirmText(''); }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600"
            >
              Cancel
            </button>
            <button
              disabled={confirmText !== 'DEPLOY'}
              onClick={() => { setConfirmProd(false); setConfirmText(''); install(); }}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-extrabold text-white disabled:opacity-40"
            >
              Install production build
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Last thing on the page, deliberately: a destructive action should be
        somewhere you arrive at, not something you pass on the way to the
        install controls. */}
    {unit && (
      <div className="mt-6">
        <UnitDangerZone
          unit={unit}
          onReleased={() => {
            // Drop the selection: this unit is no longer on an order, so the
            // install controls above it no longer describe anything real.
            setUnitId('');
            loadUnits();
          }}
        />
      </div>
    )}
    </>
  );
}
